import { useEffect, useMemo, useState } from 'react'
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { timetableApi, classesApi, subjectsApi, teachersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import PageHeader from '../components/PageHeader.jsx'
import { API_BASE_URL } from '../constants/api.js'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const SUBJECT_COLORS = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#e11d48', '#0891b2', '#ea580c', '#65a30d']
const AVAILABILITY_GREEN = '#16a34a'

const getTeacherId = (value) => value?._id || value?.id || value || ''
const getSubjectId = (value) => value?._id || value?.id || value || ''
const getTeacherName = (teacher) => teacher?.name || `${teacher?.firstName || ''} ${teacher?.lastName || ''}`.trim() || 'Teacher'
const getClassLabel = (classItem) => classItem ? `${classItem.name}${classItem.section ? ` - ${classItem.section}` : ''}` : 'Class'
const getInitials = (value = '') => value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || '?'
const isTimeOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB
const getSubjectColor = (subjectId) => {
  const hash = String(subjectId || '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return SUBJECT_COLORS[hash % SUBJECT_COLORS.length]
}
const normalizePeriodForSave = (period) => ({
  day: period.day,
  period: Number(period.period),
  startTime: period.startTime,
  endTime: period.endTime,
  subjectId: getSubjectId(period.subjectId),
  teacherId: getTeacherId(period.teacherId),
})
const resolveAvatarUrl = (value) => {
  if (!value) return ''
  if (/^(https?:)?\/\//.test(value) || value.startsWith('data:') || value.startsWith('blob:')) return value
  const base = API_BASE_URL.replace(/\/api\/?$/, '')
  return `${base}${value.startsWith('/') ? value : `/${value}`}`
}

function TeacherPickerModal({
  open,
  onClose,
  onSelect,
  teachers,
  availabilityByTeacher,
  canCheckAvailability,
  checkingAvailability,
}) {
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const filteredTeachers = useMemo(() => {
    const query = search.trim().toLowerCase()
    return teachers.filter((teacher) => {
      if (!query) return true
      return (
        getTeacherName(teacher).toLowerCase().includes(query) ||
        String(teacher?.email || '').toLowerCase().includes(query)
      )
    })
  }, [search, teachers])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[70] bg-white">
      <div className="flex h-full flex-col">
        <div className="border-b border-gray-100 bg-white px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="w-8" />
            <h3 className="flex-1 text-center text-lg font-bold text-gray-900">Select Teacher</h3>
            <button
              onClick={() => {
                setSearch('')
                onClose()
              }}
              className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or email..."
              className="w-full bg-transparent text-sm text-gray-700 outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {!canCheckAvailability ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
              Select start time and end time first to check teacher availability.
            </div>
          ) : checkingAvailability ? (
            <div className="mt-3 rounded-xl border border-primary-200 bg-primary-50 px-3 py-3 text-sm text-primary-700">
              Checking teacher availability for this time slot...
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3 pb-6">
            {filteredTeachers.length === 0 ? (
              <div className="empty-state card">
                <UserPlus className="h-10 w-10 text-gray-200" />
                <p className="empty-state-title">No teachers found</p>
                <p className="empty-state-copy">Try a different search term.</p>
              </div>
            ) : (
              filteredTeachers.map((teacher) => {
                const teacherId = getTeacherId(teacher)
                const availability = availabilityByTeacher[teacherId]
                const unavailable = canCheckAvailability && availability && !availability.available
                const firstConflict = availability?.conflicts?.[0]

                return (
                  <button
                    key={teacherId}
                    disabled={unavailable}
                    onClick={() => {
                      setSearch('')
                      onSelect(teacher)
                      onClose()
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-colors ${
                      unavailable
                        ? 'cursor-not-allowed border-red-200 bg-red-50/50 opacity-60'
                        : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-primary-50/40'
                    }`}
                  >
                    <Avatar src={resolveAvatarUrl(teacher?.avatar)} name={getTeacherName(teacher)} size="lg" className="ring-2 ring-primary-100" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-gray-900">{getTeacherName(teacher)}</p>
                      {teacher?.email ? <p className="mt-0.5 truncate text-sm text-gray-500">{teacher.email}</p> : null}
                      {canCheckAvailability && !checkingAvailability ? (
                        <p className={`mt-1 text-xs font-medium ${unavailable ? 'text-red-600' : 'text-green-700'}`}>
                          {unavailable
                            ? `Unavailable${firstConflict?.className ? ` - ${firstConflict.className}` : ''}${firstConflict?.startTime ? ` - ${firstConflict.startTime}-${firstConflict.endTime}` : ''}`
                            : 'Available for this slot'}
                        </p>
                      ) : null}
                    </div>
                    {canCheckAvailability && !checkingAvailability ? (
                      <span
                        className="rounded-full border px-3 py-1 text-xs font-semibold"
                        style={{
                          borderColor: unavailable ? '#dc2626' : AVAILABILITY_GREEN,
                          color: unavailable ? '#dc2626' : AVAILABILITY_GREEN,
                          backgroundColor: unavailable ? '#fee2e2' : '#dcfce7',
                        }}
                      >
                        {unavailable ? 'Busy' : 'Free'}
                      </span>
                    ) : null}
                  </button>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function TimetablePage() {
  const [classes, setClasses] = useState([])
  const [subjects, setSubjects] = useState([])
  const [teachers, setTeachers] = useState([])
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedDay, setSelectedDay] = useState('Monday')
  const [periods, setPeriods] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editPeriod, setEditPeriod] = useState(null)
  const [showTeacherPicker, setShowTeacherPicker] = useState(false)
  const [loadingMeta, setLoadingMeta] = useState(true)
  const [loadingTimetable, setLoadingTimetable] = useState(false)
  const [saving, setSaving] = useState(false)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [teacherAvailability, setTeacherAvailability] = useState([])

  const [periodNum, setPeriodNum] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [subjectId, setSubjectId] = useState('')
  const [selectedTeacher, setSelectedTeacher] = useState(null)

  useEffect(() => {
    let active = true

    const fetchMeta = async () => {
      try {
        setLoadingMeta(true)
        const [classRes, teacherRes] = await Promise.all([
          classesApi.getAll(),
          teachersApi.getAll(),
        ])
        if (!active) return
        setClasses(Array.isArray(classRes) ? classRes : [])
        setTeachers(Array.isArray(teacherRes) ? teacherRes : [])
      } catch (err) {
        if (active) toast.error(err.response?.data?.message || 'Failed to load timetable setup')
      } finally {
        if (active) setLoadingMeta(false)
      }
    }

    fetchMeta()
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!selectedClass) {
      setPeriods([])
      setSubjects([])
      return
    }

    let active = true

    const fetchTimetable = async () => {
      try {
        setLoadingTimetable(true)
        const [timetableRes, subjectsRes] = await Promise.all([
          timetableApi.getByClass(selectedClass),
          subjectsApi.getByClass(selectedClass),
        ])
        if (!active) return
        const nextPeriods = Array.isArray(timetableRes?.periods)
          ? timetableRes.periods.map(normalizePeriodForSave)
          : Array.isArray(timetableRes)
          ? timetableRes.map(normalizePeriodForSave)
          : []
        setPeriods(nextPeriods)
        setSubjects(Array.isArray(subjectsRes) ? subjectsRes : subjectsRes?.subjects ?? [])
      } catch (err) {
        if (!active) return
        toast.error(err.response?.data?.message || 'Failed to load timetable')
        setPeriods([])
        setSubjects([])
      } finally {
        if (active) setLoadingTimetable(false)
      }
    }

    fetchTimetable()
    return () => { active = false }
  }, [selectedClass])

  const selectedClassItem = useMemo(
    () => classes.find((item) => (item._id || item.id) === selectedClass) || null,
    [classes, selectedClass]
  )

  const canCheckTeacherAvailability = Boolean(selectedClass && selectedDay && startTime && endTime)
  const shouldFetchTeacherAvailability = canCheckTeacherAvailability && (showTeacherPicker || Boolean(selectedTeacher))

  useEffect(() => {
    if (!shouldFetchTeacherAvailability) {
      setTeacherAvailability([])
      return
    }

    let active = true

    const fetchAvailability = async () => {
      try {
        setCheckingAvailability(true)
        const data = await timetableApi.getTeacherAvailability({
          day: selectedDay,
          startTime,
          endTime,
          excludeClassId: selectedClass,
        })
        if (!active) return
        setTeacherAvailability(Array.isArray(data) ? data : [])
      } catch {
        if (active) setTeacherAvailability([])
      } finally {
        if (active) setCheckingAvailability(false)
      }
    }

    fetchAvailability()
    return () => { active = false }
  }, [endTime, selectedClass, selectedDay, selectedTeacher, shouldFetchTeacherAvailability, showTeacherPicker, startTime])

  const teacherAvailabilityByTeacher = useMemo(() => {
    const map = {}

    teacherAvailability.forEach((item) => {
      if (!item?.teacherId) return
      map[item.teacherId] = {
        available: false,
        conflicts: item.conflicts || [],
      }
    })

    if (canCheckTeacherAvailability) {
      periods.forEach((period) => {
        const teacherId = getTeacherId(period.teacherId)
        if (!teacherId) return
        if (period.day !== selectedDay) return
        if (!isTimeOverlap(startTime, endTime, period.startTime, period.endTime)) return
        if (editPeriod && period.day === selectedDay && Number(period.period) === Number(editPeriod.period)) return

        const localConflict = {
          day: period.day,
          period: period.period,
          startTime: period.startTime,
          endTime: period.endTime,
          classId: selectedClass,
          className: getClassLabel(selectedClassItem),
          subjectName:
            subjects.find((subject) => (subject._id || subject.id) === getSubjectId(period.subjectId))?.name ||
            period.subjectId?.name ||
            null,
        }

        const existing = map[teacherId]
        map[teacherId] = {
          available: false,
          conflicts: [...(existing?.conflicts || []), localConflict],
        }
      })
    }

    return map
  }, [teacherAvailability, canCheckTeacherAvailability, periods, selectedDay, startTime, endTime, editPeriod, selectedClass, selectedClassItem, subjects])

  const selectedTeacherAvailability = selectedTeacher && canCheckTeacherAvailability
    ? teacherAvailabilityByTeacher[getTeacherId(selectedTeacher)] || { available: true, conflicts: [] }
    : null

  const dayPeriods = useMemo(
    () => periods.filter((period) => period.day === selectedDay).sort((left, right) => Number(left.period) - Number(right.period)),
    [periods, selectedDay]
  )

  const totalPeriods = periods.length

  const resetForm = () => {
    setPeriodNum('')
    setStartTime('')
    setEndTime('')
    setSubjectId('')
    setSelectedTeacher(null)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditPeriod(null)
    resetForm()
  }

  const openAdd = () => {
    resetForm()
    setEditPeriod(null)
    setShowModal(true)
  }

  const openEdit = (period) => {
    setPeriodNum(String(period.period))
    setStartTime(period.startTime)
    setEndTime(period.endTime)
    setSubjectId(getSubjectId(period.subjectId))
    const teacherId = getTeacherId(period.teacherId)
    const teacher = teachers.find((item) => (item._id || item.id) === teacherId) || period.teacherId || null
    setSelectedTeacher(teacher && typeof teacher === 'object' ? teacher : null)
    setEditPeriod(period)
    setShowModal(true)
  }

  const openTeacherPicker = () => {
    if (!startTime || !endTime) {
      toast.error('Select start time and end time first')
      return
    }
    setShowTeacherPicker(true)
  }

  const handleSavePeriod = () => {
    if (!periodNum || !startTime || !endTime || !subjectId || !selectedTeacher) {
      toast.error('Please fill all fields including teacher')
      return
    }
    if (Number.isNaN(Number(periodNum)) || Number(periodNum) <= 0) {
      toast.error('Please enter a valid period number')
      return
    }
    if (endTime <= startTime) {
      toast.error('End time must be later than start time')
      return
    }

    const newPeriod = {
      day: selectedDay,
      period: Number(periodNum),
      startTime,
      endTime,
      subjectId,
      teacherId: getTeacherId(selectedTeacher),
    }

    if (editPeriod) {
      setPeriods((prev) => prev.map((period) => (
        period.day === selectedDay && Number(period.period) === Number(editPeriod.period) ? newPeriod : period
      )))
      toast.success('Period updated')
    } else {
      if (periods.some((period) => period.day === selectedDay && Number(period.period) === Number(periodNum))) {
        toast.error(`Period ${periodNum} already exists for ${selectedDay}`)
        return
      }
      setPeriods((prev) => [...prev, newPeriod])
      toast.success('Period added')
    }

    closeModal()
  }

  const handleDeletePeriod = (period) => {
    if (!window.confirm(`Remove Period ${period.period} (${selectedDay})?`)) return
    setPeriods((prev) => prev.filter((item) => !(item.day === selectedDay && Number(item.period) === Number(period.period))))
    toast.success('Period removed')
  }

  const handleSave = async () => {
    if (!selectedClass) return
    try {
      setSaving(true)
      await timetableApi.save({
        classId: selectedClass,
        periods: periods.map(normalizePeriodForSave),
      })
      toast.success('Timetable saved successfully')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save timetable')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manage Timetable"
        subtitle="Build the weekly class schedule with the same class-day-period workflow used in the Edubridge RN admin app."
        chips={[
          selectedClassItem ? getClassLabel(selectedClassItem) : 'Select a class',
          `${totalPeriods} total periods`,
          `${selectedDay} focused`,
        ]}
        actions={
          selectedClass ? (
            <button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Timetable
            </button>
          ) : null
        }
      />

      {loadingMeta ? (
        <div className="card p-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
        </div>
      ) : (
        <>
          <div className="card p-5 space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">Select Class</p>
              <div className="mt-3 overflow-x-auto">
                <div className="flex gap-2 pb-1">
                  {classes.map((classItem) => {
                    const classId = classItem._id || classItem.id
                    const active = selectedClass === classId
                    return (
                      <button
                        key={classId}
                        onClick={() => setSelectedClass(classId)}
                        className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                          active
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-primary-50/40'
                        }`}
                      >
                        {getClassLabel(classItem)}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            {selectedClassItem ? (
              selectedClassItem.classTeacher ? (
                <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4">
                  <Avatar
                    src={resolveAvatarUrl(selectedClassItem.classTeacher?.avatar)}
                    name={getTeacherName(selectedClassItem.classTeacher)}
                    size="lg"
                    className="ring-2 ring-primary-100"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">Class Teacher</p>
                    <p className="mt-1 truncate font-semibold text-gray-900">{getTeacherName(selectedClassItem.classTeacher)}</p>
                    {selectedClassItem.classTeacher?.email ? (
                      <p className="mt-0.5 truncate text-sm text-gray-500">{selectedClassItem.classTeacher.email}</p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
                  <CalendarDays className="h-4 w-4 text-gray-400" />
                  No class teacher assigned for this class
                </div>
              )
            ) : null}
          </div>

          {!selectedClass ? (
            <div className="empty-state card">
              <Clock className="h-10 w-10 text-gray-200" />
              <p className="empty-state-title">Select a class to manage its timetable</p>
              <p className="empty-state-copy">Pick a class chip above to start editing the weekly schedule.</p>
            </div>
          ) : loadingTimetable ? (
            <div className="card p-12 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
            </div>
          ) : (
            <>
              <div className="card p-4">
                <div className="overflow-x-auto">
                  <div className="flex gap-2 pb-1">
                    {DAYS.map((day) => {
                      const active = selectedDay === day
                      const count = periods.filter((period) => period.day === day).length
                      return (
                        <button
                          key={day}
                          onClick={() => setSelectedDay(day)}
                          className={`min-w-[70px] rounded-2xl border px-4 py-3 text-center transition-colors ${
                            active
                              ? 'border-primary-600 bg-primary-600 text-white'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-primary-50/40'
                          }`}
                        >
                          <p className="text-sm font-semibold">{day.slice(0, 3)}</p>
                          <p className={`mt-0.5 text-[11px] ${active ? 'text-white/80' : 'text-gray-400'}`}>{count}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedDay} · {dayPeriods.length} period{dayPeriods.length !== 1 ? 's' : ''}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">Add or adjust periods for the selected day.</p>
                  </div>
                  <button onClick={openAdd} className="btn-secondary self-start sm:self-auto">
                    <Plus className="h-4 w-4" /> Add Period
                  </button>
                </div>

                {dayPeriods.length === 0 ? (
                  <div className="card border-dashed p-10 text-center">
                    <Clock className="mx-auto h-10 w-10 text-gray-200" />
                    <p className="mt-4 text-sm font-medium text-gray-700">No periods on {selectedDay}</p>
                    <button onClick={openAdd} className="btn-primary mt-4">
                      <Plus className="h-4 w-4" /> Add First Period
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dayPeriods.map((period, index) => {
                      const subject = subjects.find((item) => (item._id || item.id) === getSubjectId(period.subjectId))
                      const teacher = teachers.find((item) => (item._id || item.id) === getTeacherId(period.teacherId))
                      const color = getSubjectColor(getSubjectId(period.subjectId))

                      return (
                        <div
                          key={`${period.day}-${period.period}-${index}`}
                          className="card border-l-4 p-4"
                          style={{ borderLeftColor: color }}
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl text-lg font-bold"
                              style={{ color, backgroundColor: `${color}18` }}
                            >
                              {period.period}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-semibold text-gray-900">
                                {subject?.name || period.subjectId?.name || 'Subject'}
                              </p>
                              <p className="mt-0.5 text-sm text-gray-500">
                                {teacher ? getTeacherName(teacher) : getTeacherName(period.teacherId)}
                              </p>
                              <p className="mt-0.5 text-xs text-gray-400">
                                {period.startTime} - {period.endTime}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => openEdit(period)} className="rounded-xl border border-primary-100 bg-primary-50 p-2 text-primary-600 transition-colors hover:bg-primary-100" title="Edit period">
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleDeletePeriod(period)} className="rounded-xl border border-red-100 bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100" title="Delete period">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <button onClick={handleSave} disabled={saving} className="btn-primary w-full">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Timetable ({totalPeriods} total period{totalPeriods === 1 ? '' : 's'})
              </button>
            </>
          )}
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[60] bg-black/50">
          <div className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-[28px] bg-white pb-8 shadow-2xl">
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-gray-200" />
            <div className="flex items-center justify-between gap-4 px-6 py-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{editPeriod ? 'Edit Period' : `Add Period - ${selectedDay}`}</h3>
                <p className="mt-1 text-sm text-gray-500">Set the period number, timing, subject, and teacher for this slot.</p>
              </div>
              <button onClick={closeModal} className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-5 px-6">
              <div className="grid gap-4 sm:grid-cols-[110px_minmax(0,1fr)_minmax(0,1fr)]">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">Period No.</label>
                  <input
                    value={periodNum}
                    onChange={(event) => setPeriodNum(event.target.value)}
                    placeholder="1"
                    inputMode="numeric"
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">Start Time</label>
                  <input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} className="input-field w-full" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-500">End Time</label>
                  <input type="time" value={endTime} onChange={(event) => setEndTime(event.target.value)} className="input-field w-full" />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-gray-500">Subject *</label>
                {subjects.length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-500">
                    No subjects found for this class.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {subjects.map((subject) => {
                      const active = subjectId === (subject._id || subject.id)
                      return (
                        <button
                          key={subject._id || subject.id}
                          onClick={() => setSubjectId(subject._id || subject.id)}
                          className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                            active
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:bg-primary-50/40'
                          }`}
                        >
                          {subject.name}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-gray-500">Teacher *</label>
                {selectedTeacher ? (
                  <>
                    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-4">
                      <Avatar src={resolveAvatarUrl(selectedTeacher?.avatar)} name={getTeacherName(selectedTeacher)} size="lg" className="ring-2 ring-primary-100" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-gray-900">{getTeacherName(selectedTeacher)}</p>
                        {selectedTeacher?.email ? <p className="mt-0.5 truncate text-sm text-gray-500">{selectedTeacher.email}</p> : null}
                      </div>
                      <button onClick={openTeacherPicker} className="rounded-xl border border-primary-200 px-3 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50">
                        Change
                      </button>
                    </div>
                    {canCheckTeacherAvailability && selectedTeacherAvailability ? (
                      <p className={`mt-2 text-sm font-medium ${selectedTeacherAvailability.available ? 'text-green-700' : 'text-red-600'}`}>
                        {selectedTeacherAvailability.available
                          ? 'Available for this selected time slot'
                          : `Unavailable${selectedTeacherAvailability.conflicts?.[0]?.className ? ` - ${selectedTeacherAvailability.conflicts[0].className}` : ''}${selectedTeacherAvailability.conflicts?.[0]?.startTime ? ` - ${selectedTeacherAvailability.conflicts[0].startTime}-${selectedTeacherAvailability.conflicts[0].endTime}` : ''}`}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <button
                    onClick={openTeacherPicker}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary-300 bg-primary-50 px-4 py-4 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
                  >
                    <UserPlus className="h-4 w-4" />
                    Select Teacher
                  </button>
                )}
              </div>

              <div className="flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:justify-end">
                <button onClick={closeModal} className="btn-secondary">Cancel</button>
                <button onClick={handleSavePeriod} className="btn-primary">
                  {editPeriod ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {editPeriod ? 'Update Period' : 'Add Period'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TeacherPickerModal
        open={showTeacherPicker}
        onClose={() => setShowTeacherPicker(false)}
        onSelect={(teacher) => setSelectedTeacher(teacher)}
        teachers={teachers}
        availabilityByTeacher={teacherAvailabilityByTeacher}
        canCheckAvailability={canCheckTeacherAvailability}
        checkingAvailability={checkingAvailability}
      />
    </div>
  )
}
